import React from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import getRoute from '@root/src/services/routeList';
import { REGISTRATION_STATUS } from '@root/src/types';
import { formatDateWithHours, formatEventDate } from '@utils/dates';
import Icon, { ICON_NAME } from '@features/ui/components/Icon';
import StackableCard, { StackableCardSection } from '@features/ui/components/StackableCard';
import Button, { BUTTON_VARIANT } from '@features/ui/components/Button';
import { textP, textSmall } from '@features/ui/styles/textStyles';
import { selectEventDetail } from '@features/eventDetail/slices/eventDetailServerSlice';
import { formatFullLocation, getQueryEventId } from '@features/eventDetail/utils';
import { sportDescriptionMapping } from '@features/eventList/components/EventCard';

interface EventSidePanelSectionProps {
  icon: ICON_NAME;
  title: string;
  children?: React.ReactNode;
}

const EventSidePanelSection = ({ icon, title, children }: EventSidePanelSectionProps) => (
  <SectionContainer>
    <SectionIcon name={icon} color="var(--color-grey3)" size="20px" />
    <SectionTitle>{title}</SectionTitle>
    <SectionContent>{children}</SectionContent>
  </SectionContainer>
);

interface EventSidePanelProps {
  className?: string;
  hideSubscribeButton?: boolean;
}

const EventSidePanel = ({ className, hideSubscribeButton }: EventSidePanelProps) => {
  const router = useRouter();
  const { t } = useTranslation();

  const event = useSelector(selectEventDetail(getQueryEventId(router.query)));
  const {
    date,
    dateFrom,
    dateTo,
    urlOrganiser,
    location,
    site,
    eventType,
    sportCategory,
  } = event;

  const { registrationFrom, registrationTo, registration } = event.status;

  const showRegistration = registration === REGISTRATION_STATUS.OPEN && !hideSubscribeButton && !event.quotas?.full;

  const sport = `${eventType} - ${t(sportDescriptionMapping[sportCategory])}`;

  return (
    <CardContainer className={className}>
      <SectionsContainer>
        <EventSidePanelSection icon={ICON_NAME.dateRange} title={t('eventSidePanel_sectionDateTitle.message')}>
          {formatEventDate(date, dateFrom, dateTo)}
        </EventSidePanelSection>
        <EventSidePanelSection icon={ICON_NAME.pin} title={t('eventSidePanel_sectionLocationTitle.message')}>
          {formatFullLocation(location, site)}
        </EventSidePanelSection>
        <EventSidePanelSection icon={ICON_NAME.sort} title={t('eventSidePanel_sectionSportTitle.message')}>
          {sport}
        </EventSidePanelSection>
        <EventSidePanelSection
          icon={ICON_NAME.playlistAddCheck}
          title={t('eventSidePanel_sectionSubscriptionsTitle.message')}
        >
          <InscriptionsContent>
            <p>{formatDateWithHours(registrationFrom)}</p>
            <p>{formatDateWithHours(registrationTo)}</p>
            {showRegistration && (
              <Button
                url={getRoute.events(event.slug).subscriptions()}
                leftIcon={ICON_NAME.playlistAddCheck}
                text={t('eventSidePanel_subscribeBtn.message')}
                urlScroll={false}
              />
            )}
          </InscriptionsContent>
        </EventSidePanelSection>
        <EventSidePanelSection icon={ICON_NAME.outlinedFlag} title={t('eventSidePanel_sectionEngagedListTitle.message')}>
          {event.showStartListFrom ? formatDateWithHours(event.showStartListFrom) : '-'}
        </EventSidePanelSection>
      </SectionsContainer>
      <StackableCardSection>
        {event.serie.isChampionship && (
          <Button
            variant={BUTTON_VARIANT.link}
            leftIcon={ICON_NAME.emojiEvent}
            text={t('eventSidePanel_seriePageLink.message')}
            onClick={() => router.push(getRoute.series(event.serie.slug).description(), null)}
          />
        )}
        {urlOrganiser && (
          <Button
            url={{ pathname: urlOrganiser }}
            variant={BUTTON_VARIANT.link}
            leftIcon={ICON_NAME.computer}
            text={t('eventSidePanel_organiserLink.message')}
            urlTargetBlank
          />
        )}
      </StackableCardSection>
    </CardContainer>
  );
};

export default EventSidePanel;

const CardContainer = styled(StackableCard)`
  width: 351px;
  padding: 10px;
  height: fit-content;
`;

const SectionsContainer = styled(StackableCardSection)`
  > *:not(:last-child) {
    margin-bottom: 20px;
  }
`;

const InscriptionsContent = styled.div`
  display: flex;
  flex-direction: column;

  > *:last-child {
    margin-top: 12px;
  }
`;

const SectionContainer = styled.div`
  display: grid;
  grid-column-gap: 8px;
  grid-row-gap: 8px;
  grid-template-columns: 20px auto;
  grid-template-areas: 
        "section-icon section-title"
        ". section-content";

  > *:not(:last-child) {
    color: var(--color-grey3);
  }
`;

const SectionIcon = styled(Icon)`
  grid-area: section-icon;
`;

const SectionTitle = styled.div`
  grid-area: section-title;
  ${textSmall};
  text-transform: uppercase;
`;

const SectionContent = styled.div`
  grid-area: section-content;
  ${textP};
  color: var(--color-grey4);
`;
